/**
 * Memory Monitor Utility
 * Tracks memory usage during CSV processing to prevent browser crashes
 *
 * Features:
 * - Real-time memory usage tracking
 * - Configurable memory limits
 * - Chrome-specific memory API with fallback
 * - File size pre-validation
 */

export interface MemoryStatus {
  safe: boolean
  used: number
  limit: number
  percentage: number
  message?: string
}

export interface MemoryMonitorOptions {
  limitMB?: number
  checkInterval?: number // How often to check (in operations)
}

/**
 * Memory monitor for tracking resource usage during processing
 */
export class MemoryMonitor {
  private initialMemory: number
  private limitMB: number
  private checkInterval: number
  private operationCount: number = 0

  constructor(options: MemoryMonitorOptions = {}) {
    this.limitMB = options.limitMB || 200
    this.checkInterval = options.checkInterval || 100
    this.initialMemory = this.getMemoryUsage()
  }

  /**
   * Get current memory usage in bytes
   */
  private getMemoryUsage(): number {
    // @ts-ignore - performance.memory is Chrome-specific
    if (typeof performance !== 'undefined' && performance.memory) {
      // @ts-ignore
      return performance.memory.usedJSHeapSize
    }
    // In non-Chrome environments, return 0 (no memory monitoring)
    return 0
  }

  /**
   * Check if file size is within limits
   */
  validateFileSize(file: File): MemoryStatus {
    const fileSizeMB = file.size / (1024 * 1024)
    const limitBytes = this.limitMB * 1024 * 1024
    const percentage = (file.size / limitBytes) * 100

    if (fileSizeMB > this.limitMB) {
      return {
        safe: false,
        used: file.size,
        limit: limitBytes,
        percentage,
        message: `File size (${Math.round(fileSizeMB)}MB) exceeds memory limit (${this.limitMB}MB)`,
      }
    }

    return {
      safe: true,
      used: file.size,
      limit: limitBytes,
      percentage,
    }
  }

  /**
   * Check current memory usage
   */
  checkMemory(): MemoryStatus {
    const current = this.getMemoryUsage()
    const delta = current - this.initialMemory
    const limitBytes = this.limitMB * 1024 * 1024
    const percentage = (delta / limitBytes) * 100

    if (delta > limitBytes) {
      return {
        safe: false,
        used: delta,
        limit: limitBytes,
        percentage,
        message: `Memory usage (${Math.round(delta / 1024 / 1024)}MB) exceeds limit (${this.limitMB}MB)`,
      }
    }

    return {
      safe: true,
      used: delta,
      limit: limitBytes,
      percentage,
    }
  }

  /**
   * Check memory periodically based on operation count
   * Returns true if should check memory now
   */
  shouldCheckMemory(): boolean {
    this.operationCount++
    return this.operationCount % this.checkInterval === 0
  }

  /**
   * Get current memory usage in MB
   */
  getUsageMB(): number {
    const current = this.getMemoryUsage()
    const delta = current - this.initialMemory
    return Math.round(delta / 1024 / 1024)
  }

  /**
   * Get memory usage as percentage of limit
   */
  getUsagePercentage(): number {
    const current = this.getMemoryUsage()
    const delta = current - this.initialMemory
    const limitBytes = this.limitMB * 1024 * 1024
    return Math.round((delta / limitBytes) * 100)
  }

  /**
   * Reset the memory baseline
   */
  reset(): void {
    this.initialMemory = this.getMemoryUsage()
    this.operationCount = 0
  }

  /**
   * Check if browser supports memory monitoring
   */
  static isSupported(): boolean {
    // @ts-ignore - performance.memory is Chrome-specific
    return (
      typeof performance !== 'undefined' &&
      'memory' in performance &&
      performance.memory !== undefined
    )
  }

  /**
   * Estimate memory usage for a given number of contacts
   * Useful for pre-flight checks
   */
  static estimateContactMemoryMB(contactCount: number): number {
    const avgContactSizeKB = 2.5 // Average size per contact in KB
    const indexOverhead = 1.3 // 30% overhead for indexes and processing
    return Math.round((contactCount * avgContactSizeKB * indexOverhead) / 1024)
  }
}
